import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Modal,
} from "@mui/material";
import axios from "axios";

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 500,
    bgcolor: "#16213E",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
    color: "#EFFFFD",
    textAlign: "center",
};

const keypadButtonStyle = {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    fontSize: "36px",
    fontWeight: "bold",
    backgroundColor: "#1A1A2E",
    color: "#FFFFFF",
    border: "2px solid #FFFFFF",
    margin: "8px",
    cursor: "pointer",
};

const ClockInComponent = () => {
    const [employeeNumber, setEmployeeNumber] = useState("");
    const [user, setUser] = useState(null);
    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isClockedIn, setIsClockedIn] = useState(false);
    const [clockInTime, setClockInTime] = useState(null);
    const [timeCardRecord, setTimeCardRecord] = useState(null);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [confirmationData, setConfirmationData] = useState({});

    // Fetch list of sites on mount
    useEffect(() => {
        axios
            .get("https://figazuresql.swancreate.com/api/swa/SchoolMst")
            .then((response) => setSites(response.data))
            .catch((error) =>
                console.error("Error fetching site data:", error)
            );
    }, []);

    // Helper: return today's date in YYYY-MM-DD format
    const getTodayDateStr = () => {
        const now = new Date();
        return now.toISOString().split("T")[0];
    };

    // Check for an open (incomplete) session for today
    const checkOpenSession = async (userId) => {
        try {
            const response = await axios.get(
                `https://figazuresql.swancreate.com/api/swa/TimeCard?UserID=${userId}`
            );
            const timecards = response.data || [];
            const todayStr = getTodayDateStr();

            const openCard = timecards.find((tc) => {
                if (!tc.ClockInTime) return false;
                if (tc.ClockOutTime !== null) return false;
                const clockInDateStr = new Date(tc.ClockInTime)
                    .toISOString()
                    .split("T")[0];
                return clockInDateStr === todayStr;
            });

            if (openCard) {
                setIsClockedIn(true);
                setClockInTime(new Date(openCard.ClockInTime));
                setTimeCardRecord(openCard);
            } else {
                setIsClockedIn(false);
                setClockInTime(null);
                setTimeCardRecord(null);
            }
        } catch (err) {
            console.error("Error checking open session:", err);
        }
    };

    // Update employee number input (with no max length)
    const handleEmployeeNumberChange = (e) => {
        const newValue = typeof e === "string" ? e : e.target.value;
        setEmployeeNumber(newValue);
    };

    // Handle keypad digit press (no length restrictions)
    const handleKeypadDigit = (digit) => {
        setEmployeeNumber(employeeNumber + digit);
    };

    // Backspace last digit
    const handleBackspace = () => {
        if (employeeNumber.length > 0) {
            setEmployeeNumber(employeeNumber.slice(0, -1));
        }
    };

    // Clear the entire employee number
    const handleClear = () => {
        setEmployeeNumber("");
    };

    // Submit the employee number to retrieve employee info
    const handleEmployeeNumberSubmit = async () => {
        if (!employeeNumber) return;
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                "https://figazuresql.swancreate.com/api/swa/EmployeeTimecard"
            );
            // Find the employee using EmployeeNumber
            const foundUser = response.data.find(
                (u) => u.EmployeeNumber === employeeNumber
            );
            if (foundUser) {
                setUser(foundUser);
                setSelectedSite(foundUser.SiteAssignedID || "");
                await checkOpenSession(foundUser.apiid);
            } else {
                setError("Employee not found with that number.");
            }
        } catch (err) {
            console.error("Error fetching employee data:", err);
            setError("Error fetching employee data.");
        }
        setLoading(false);
    };

    // Handle Clock In/Out
    const handleClockInOut = async () => {
        if (!isClockedIn) {
            // Clock In
            const now = new Date();
            setClockInTime(now);
            setIsClockedIn(true);

            try {
                const siteObj = sites.find(
                    (site) => site.SchoolId === selectedSite
                );
                const payload = {
                    UserID: user.apiid,
                    EmployeeName: user.EmployeeName, // Display full name
                    EmployeeNumber: user.EmployeeNumber, // Export EmployeeNumber to TimeCard table
                    ClockInTime: now,
                    SiteID: selectedSite,
                    SiteName: siteObj ? siteObj.SchoolName : "",
                    HourlyRate: user.BaseRate || 0, // Use BaseRate for rate
                };
                const res = await axios.post(
                    "https://figazuresql.swancreate.com/api/swa/TimeCard",
                    payload
                );

                setTimeCardRecord(res.data);
                setConfirmationData({
                    action: "Clock In",
                    time: now,
                    site: siteObj ? siteObj.SchoolName : "",
                    employee: user.EmployeeName,
                });
                setConfirmationOpen(true);
            } catch (err) {
                console.error("Error clocking in:", err);
                setError("Clock in failed.");
            }
        } else {
            // Clock Out
            const now = new Date();
            const hoursWorked =
                (now - clockInTime) / (1000 * 60 * 60);

            try {
                const payload = {
                    ClockOutTime: now,
                    TimeWorked: hoursWorked,
                    TotalPay: hoursWorked * (user.BaseRate || 0),
                };
                await axios.put(
                    `https://figazuresql.swancreate.com/api/swa/TimeCard/${timeCardRecord.apiid}`,
                    payload
                );

                setConfirmationData({
                    action: "Clock Out",
                    time: now,
                    hoursWorked: hoursWorked.toFixed(2),
                    employee: user.EmployeeName,
                });
                setConfirmationOpen(true);

                setIsClockedIn(false);
                setClockInTime(null);
                setTimeCardRecord(null);
            } catch (err) {
                console.error("Error clocking out:", err);
                setError("Clock out failed.");
            }
        }
    };

    // Close confirmation modal and refresh the page after any submit occurs.
    const handleCloseModal = () => {
        setConfirmationOpen(false);
        window.location.reload();
    };

    return (
        <Box
            sx={{
                backgroundColor: "#1A1A2E",
                color: "#EFFFFD",
                minHeight: "100vh",
                padding: "40px",
                borderRadius: "20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Typography
                variant="h3"
                sx={{
                    fontWeight: "bold",
                    mb: 2,
                    fontSize: "48px",
                    color: "#ADD8E6", // Light blue
                    whiteSpace: "nowrap",
                }}
            >
                {user ? `Welcome, ${user.EmployeeName}` : "Clock In/Out"}
            </Typography>

            {/* Show Employee Number input and keypad if no employee is loaded */}
            {!user && (
                <Box sx={{ width: "100%", maxWidth: "400px", mb: 4 }}>
                    <TextField
                        label="Enter Employee Number"
                        variant="outlined"
                        fullWidth
                        value={employeeNumber}
                        onChange={handleEmployeeNumberChange}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleEmployeeNumberSubmit();
                            }
                        }}
                        autoFocus
                        inputProps={{
                            style: {
                                backgroundColor: "#121212",
                                color: "#FFFFFF",
                                fontWeight: "bold",
                                fontSize: "36px",
                                textAlign: "center",
                                letterSpacing: "5px",
                            },
                        }}
                        sx={{
                            mt: 2,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                border: "2px solid #fff",
                            },
                            "& .MuiInputLabel-root": {
                                color: "#FFFFFF",
                                fontSize: "24px",
                                fontWeight: "bold",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FFFFFF !important",
                            },
                        }}
                    />

                    {loading && (
                        <Typography variant="h5" sx={{ mt: 2 }}>
                            Loading...
                        </Typography>
                    )}
                    {error && (
                        <Typography variant="h5" color="error" sx={{ mt: 2 }}>
                            {error}
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        fullWidth
                        sx={{
                            mt: 4,
                            backgroundColor: "#1E90FF",
                            color: "#121212",
                            fontSize: "28px",
                            padding: "16px",
                            borderRadius: "12px",
                        }}
                        onClick={handleEmployeeNumberSubmit}
                    >
                        Submit
                    </Button>

                    {/* Keypad Container with White Glow */}
                    <Box
                        sx={{
                            mt: 4,
                            p: 2,
                            borderRadius: "12px",
                            boxShadow: "0 0 10px 5px white",
                        }}
                    >
                        <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
                            Tap to enter Employee Number:
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            {[1, 2, 3].map((val) => (
                                <Button
                                    key={val}
                                    sx={keypadButtonStyle}
                                    onClick={() => handleKeypadDigit(val)}
                                >
                                    {val}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            {[4, 5, 6].map((val) => (
                                <Button
                                    key={val}
                                    sx={keypadButtonStyle}
                                    onClick={() => handleKeypadDigit(val)}
                                >
                                    {val}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            {[7, 8, 9].map((val) => (
                                <Button
                                    key={val}
                                    sx={keypadButtonStyle}
                                    onClick={() => handleKeypadDigit(val)}
                                >
                                    {val}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Button sx={keypadButtonStyle} onClick={handleBackspace}>
                                ←
                            </Button>
                            <Button sx={keypadButtonStyle} onClick={() => handleKeypadDigit(0)}>
                                0
                            </Button>
                            <Button sx={keypadButtonStyle} onClick={handleClear}>
                                C
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* After successful login, show site selection and clock in/out button */}
            {user && (
                <Box sx={{ width: "100%", maxWidth: "400px" }}>
                    <TextField
                        select
                        label="Select Site"
                        fullWidth
                        value={selectedSite}
                        onChange={(e) => setSelectedSite(e.target.value)}
                        sx={{
                            mt: 2,
                            "& .MuiInputLabel-root": { fontSize: "28px" },
                            "& .MuiOutlinedInput-root": {
                                fontSize: "28px",
                                borderRadius: "12px",
                            },
                        }}
                    >
                        {sites.map((site) => (
                            <MenuItem
                                key={site.SchoolId}
                                value={site.SchoolId}
                                sx={{ fontSize: "28px" }}
                            >
                                {site.SchoolName}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Button
                        variant="contained"
                        fullWidth
                        sx={{
                            mt: 4,
                            backgroundColor: isClockedIn ? "#FF4136" : "#00CED1",
                            color: "#121212",
                            fontSize: "28px",
                            padding: "16px",
                            borderRadius: "12px",
                        }}
                        onClick={handleClockInOut}
                    >
                        {isClockedIn ? "Clock Out" : "Clock In"}
                    </Button>
                </Box>
            )}

            {/* Confirmation Modal */}
            <Modal
                open={confirmationOpen}
                onClose={handleCloseModal}
                aria-labelledby="confirmation-modal-title"
                aria-describedby="confirmation-modal-description"
            >
                <Box sx={modalStyle}>
                    {confirmationData.action === "Clock In" && (
                        <>
                            <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }}>
                                Clocked In Successfully!
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                Employee: {confirmationData.employee}
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                Time In: {new Date(confirmationData.time).toLocaleTimeString()}
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                Site: {confirmationData.site}
                            </Typography>
                        </>
                    )}

                    {confirmationData.action === "Clock Out" && (
                        <>
                            <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }}>
                                Clocked Out Successfully!
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                Employee: {confirmationData.employee}
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                Time Out: {new Date(confirmationData.time).toLocaleTimeString()}
                            </Typography>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                Hours Worked: {confirmationData.hoursWorked}
                            </Typography>
                        </>
                    )}

                    <Button
                        variant="contained"
                        sx={{
                            mt: 3,
                            backgroundColor: "#1E90FF",
                            color: "#121212",
                            fontSize: "24px",
                            padding: "12px",
                            borderRadius: "12px",
                        }}
                        onClick={handleCloseModal}
                    >
                        Close
                    </Button>
                </Box>
            </Modal>

            {error && (
                <Typography variant="h5" color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default ClockInComponent;
